import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { useFilterParams } from '../../hooks';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { orderAPI, bankAccountAPI, orderStaffAuditAPI, orderApprovalAPI, employeeAPI } from '../../services';
import { toast } from 'react-toastify';
import { format, subDays } from 'date-fns';
import PaginationComponent from '../../components/PaginationComponent';
import DateRangePicker from '../../components/fields/DateRangePicker';

const ORDER_STATUS = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'];

const PAYMENT_METHODS = ['CASH', 'BANK_TRANSFER', 'CARD', 'MOMO', 'VNPAY'];

const ORDER_STATUS_LABELS = {
  PENDING: 'Chờ xác nhận',
  CONFIRMED: 'Đã xác nhận',
  PREPARING: 'Đang chuẩn bị',
  READY: 'Sẵn sàng phục vụ',
  SERVED: 'Đã phục vụ',
  COMPLETED: 'Hoàn thành',
  CANCELLED: 'Đã hủy',
};

const PAYMENT_STATUS_LABELS = {
  PENDING: 'Chưa thanh toán',
  PAID: 'Đã thanh toán',
  REFUNDED: 'Đã hoàn tiền',
};

const PAYMENT_METHOD_LABELS = {
  CASH: 'Tiền mặt',
  BANK_TRANSFER: 'Chuyển khoản',
  CARD: 'Thẻ',
  MOMO: 'MoMo',
  VNPAY: 'VNPay',
};

const ORDER_STAFF_ACTION_LABELS = {
  LINE_ITEMS_ADDED: 'Thêm món',
  DELETE_LINE_ITEMS_REQUESTED: 'Yêu cầu xóa món',
  LINE_ITEMS_REMOVED: 'Đã xóa món (duyệt)',
  // #143811: từ 01/09/2026 SĐT khách hiện đầy đủ cho mọi vai trò, không còn nút
  // "Xem SĐT đầy đủ" — server ghi vết ÂM THẦM mỗi lượt mở chi tiết đơn (chống
  // trùng 10 phút/người/đơn), và đây là chỗ quản lý đọc được log đó.
  CUSTOMER_PHONE_VIEWED: 'Xem SĐT khách',
  // Sau khi #211929 sửa lỗi bóc dữ liệu (xem hàm pickAuditEvents), bảng nhật ký
  // mới thật sự hiện ra —
  // kéo theo 5 loại dưới đây lần đầu lộ diện dưới dạng chuỗi enum trơ. Thêm
  // nhãn cho đủ ORDER_STAFF_ACTION của server, đừng để quản lý đọc enum.
  PAYMENT_OTP_REQUESTED: 'Gửi OTP thanh toán',
  PAYMENT_OTP_VERIFIED: 'Xác nhận OTP thanh toán',
  PAYMENT_OTP_FAILED: 'OTP thanh toán sai',
  PAYMENT_OTP_FALLBACK_TELEGRAM: 'OTP chuyển sang Telegram',
  PAYMENT_OTP_SKIPPED: 'Bỏ qua OTP thanh toán',
  TABLE_TRANSFERRED: 'Chuyển bàn',
};

/**
 * #211929: `apiClient` đã trả thẳng thân phản hồi, nên `/order-staff-audit/events`
 * về tay ta là `{ data: [...] }` — mảng nằm NGAY ở `.data`. Code cũ bóc thêm một
 * tầng (`auditRes.data.data`) nên bảng "Nhật ký thao tác" luôn rỗng. Nhận cả 3
 * dạng để không phụ thuộc vào việc API có bọc thêm hay không.
 */
const pickAuditEvents = (auditRes) => {
  if (Array.isArray(auditRes)) return auditRes;
  const lv1 = auditRes?.data ?? auditRes?.events;
  if (Array.isArray(lv1)) return lv1;
  const lv2 = lv1?.data ?? lv1?.events;
  return Array.isArray(lv2) ? lv2 : [];
};

/**
 * Bảng "Yêu cầu xóa đơn / xóa món" dán nhãn mọi `request_type` khác DELETE_ORDER
 * thành "Xóa món". #211929 từng sinh 1 OrderApprovalRequest (VIEW_CUSTOMER_PHONE)
 * mỗi lượt NV xem SĐT — nếu không lọc thì nó hiện thành yêu cầu xóa món giả.
 * #143811 đã gỡ hẳn luồng đó nên KHÔNG còn dòng mới, nhưng dòng CŨ vẫn nằm trong
 * CSDL nên bộ lọc này phải giữ.
 * Chỉ giữ đúng 2 loại mà bảng này nhận trách nhiệm hiển thị.
 */
const DELETION_REQUEST_TYPES = ['DELETE_ORDER', 'DELETE_ORDER_ITEM'];
const pickDeletionRequests = (dr) =>
  (Array.isArray(dr) ? dr : []).filter((r) =>
    DELETION_REQUEST_TYPES.includes(r?.request_type),
  );

const ORDER_TYPE_LABELS = {
  DINE_IN: 'Tại quán',
  TAKEAWAY: 'Mang đi',
  DELIVERY: 'Giao hàng',
};

const statusVariant = {
  PENDING: 'warning',
  CONFIRMED: 'info',
  PREPARING: 'primary',
  READY: 'secondary',
  SERVED: 'secondary',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

const paymentVariant = {
  PENDING: 'warning',
  PAID: 'success',
  REFUNDED: 'info',
};

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Number(value));
};

/** Trả về số ≥ 0 hoặc undefined nếu để trống / không hợp lệ (gửi query API). */
const parseOptionalMaxTotalAmount = (value) => {
  const s = String(value ?? '').replace(/\s/g, '');
  if (s === '' || Number.isNaN(Number(s)) || Number(s) < 0) return undefined;
  return Number(s);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
};

const normalizeOrderRecord = (order) => {
  if (!order) return null;
  return {
    id: order.id,
    orderNumber: order.order_number || order.orderNumber,
    branchId: order.branch_id || order.branchId,
    branchName: order.branch?.name || order.branch_name || order.branchName,
    tableId: order.table_id || order.tableId,
    tableLabel: order.table?.number || order.table?.name || order.table_id || '-',
    orderType: order.order_type || order.orderType,
    status: order.status,
    paymentStatus: order.payment_status || order.paymentStatus,
    paymentMethod: order.payment_method || order.paymentMethod,
    customerName: order.customer_name || order.customerName,
    customerPhone: order.customer_phone || order.customerPhone,
    memberCount: order.member_count ?? order.memberCount ?? null,
    totalAmount: Number(order.total_amount ?? order.totalAmount ?? 0),
    createdAt: order.created_at || order.createdAt,
    paidAt: order.paid_at || order.paidAt,
    // #182157: NV đã thanh toán (thu tiền) đơn — Order.completed_by + tên do máy chủ tra sẵn.
    completedBy: order.completed_by || order.completedBy || null,
    completedByName: order.completer?.full_name || order.completedByName || null,
    paymentProofUrl: order.payment_proof_url || order.paymentProofUrl,
    cancelReason: order.cancel_reason ?? order.cancelReason ?? null,
    cancelledAt: order.cancelled_at ?? order.cancelledAt ?? null,
    mergedFromOrderIds: order.merged_from_order_ids ?? order.mergedFromOrderIds ?? [],
    mergedIntoOrderId: order.merged_into_order_id ?? order.mergedIntoOrderId ?? null,
  };
};

const translateLabel = (dict, value, fallback) => {
  if (!value) return fallback || '-';
  return dict[value] || fallback || value;
};

const SORT_FIELDS = [
  { value: 'created_at', label: 'Ngày tạo' },
  { value: 'paid_at', label: 'Ngày thanh toán' },
  { value: 'order_number', label: 'Mã đơn' },
];
const SORT_ORDERS = [
  { value: 'desc', label: 'Giảm dần' },
  { value: 'asc', label: 'Tăng dần' },
];

// Bộ lọc KHÔNG lên URL: hoặc quá phụ (sort), hoặc là id nội bộ ít ai dán lại.
const getDefaultLocalFilters = () => ({
  tableId: '',
  paymentMethod: '',
  bankAccountId: '',
  completedBy: '',
  maxTotalAmount: '',
  hasDeletionRequest: false,
  sortBy: 'created_at',
  sortOrder: 'desc',
});

// #120936 — bộ lọc CÓ lên URL. Tên theo khuôn chốt: `status`,`from`,`to`,`q`,`page`,`limit`.
// `branch_id` do BranchContext quản chung. Trong trang, `from`/`to` vẫn mang tên cũ
// `startDate`/`endDate` (đó là tên tham số của `orderAPI.getOrders`) — chỉ ĐỔI TÊN Ở URL.
const defaultFrom = () => format(subDays(new Date(), 6), 'yyyy-MM-dd');
const defaultTo = () => format(new Date(), 'yyyy-MM-dd');
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));
const isPositiveInt = (v) => /^\d+$/.test(v) && Number(v) >= 1;

const ORDER_FILTER_SPEC = {
  status: {
    default: 'COMPLETED',
    parse: (v) => v.toUpperCase(),
    valid: (v) => v.toUpperCase() === 'ALL' || ORDER_STATUS.includes(v.toUpperCase()),
  },
  from: { default: defaultFrom, valid: isIsoDate },
  to: { default: defaultTo, valid: isIsoDate },
  // Ô tìm ở màn này là MÃ ĐƠN — không phải dữ liệu cá nhân, đưa lên URL được.
  // #131331 — tên trên URL là `q` (chốt dùng chung 4 mảng); `search` giữ làm alias CHỈ-ĐỌC
  // để link cũ không gãy. Tầng API vẫn nhận `orderNumber` như trước, không đụng.
  q: { default: '', alias: ['search'], debounce: 350 },
  page: { default: 1, parse: Number, valid: isPositiveInt },
  limit: { default: 20, parse: Number, valid: (v) => ['10', '20', '50'].includes(v) },
  // #071245 — CHẾ ĐỘ SOI DOANH THU. Là `bucket_key` của một dòng bảng "Doanh thu
  // theo tài khoản ngân hàng": id tài khoản, `CASH`, hoặc `UNKNOWN_BANK`. Nằm trên
  // URL nên link mở lại/chia sẻ vẫn ra đúng tập đơn.
  bank_bucket: { default: '' },
};

const OrderListPage = () => {
  const { selectedBranch } = useBranch();
  const [urlFilters, setUrlFilters, resetUrlFilters, { ignored, dismissIgnored }] =
    useFilterParams(ORDER_FILTER_SPEC);
  const [localFilters, setLocalFilters] = useState(getDefaultLocalFilters);

  // Ghép lại đúng hình dạng `filters` mà cả trang đang dùng, để phần dưới không phải sửa gì.
  const filters = useMemo(
    () => ({
      ...localFilters,
      status: urlFilters.status,
      orderNumber: urlFilters.q,
      startDate: urlFilters.from,
      endDate: urlFilters.to,
      bankBucket: urlFilters.bank_bucket,
    }),
    [localFilters, urlFilters],
  );
  // Bật chế độ soi doanh thu: máy chủ tự chọn tập đơn theo đúng luật của báo cáo,
  // nên các bộ lọc thường (trạng thái, phương thức, bàn...) không còn tác dụng.
  const bankBucketMode = Boolean(urlFilters.bank_bucket);
  const page = urlFilters.page;
  const limit = urlFilters.limit;

  // Tên trường trong trang -> tên param trên URL.
  const URL_FIELDS = { status: 'status', orderNumber: 'q', startDate: 'from', endDate: 'to' };
  const setPage = useCallback(
    (next) => setUrlFilters((prev) => ({ page: typeof next === 'function' ? next(prev.page) : next })),
    [setUrlFilters],
  );
  const [orders, setOrders] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payerOptions, setPayerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // #232308: số đầy đủ vừa lấy về từ lượt bấm nút "Xem SĐT" (không lưu sang đơn khác).
  const [revealedPhone, setRevealedPhone] = useState(null);
  const [revealingPhone, setRevealingPhone] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoadPaymentProof, setDetailLoadPaymentProof] = useState(false);
  const [loadAllProofImages, setLoadAllProofImages] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [orderAuditEvents, setOrderAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [deletionLoading, setDeletionLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const maxTotalAmount = parseOptionalMaxTotalAmount(filters.maxTotalAmount);

      // #071245 — ở chế độ soi doanh thu, CHỈ gửi `revenueBankBucket` + kỳ + chi
      // nhánh. Cố tình KHÔNG kèm `status`/`paymentMethod`/... : báo cáo doanh thu
      // không lọc theo mấy trường đó, kèm vào là tập đơn hẹp lại và tổng tiền
      // không còn khớp con số đã bấm.
      const params = filters.bankBucket
        ? {
            revenueBankBucket: filters.bankBucket,
            startDate: filters.startDate,
            endDate: filters.endDate,
            branchId: selectedBranch?.id,
            sortBy: filters.sortBy || 'created_at',
            sortOrder: filters.sortOrder || 'desc',
            page,
            limit,
          }
        : {
            startDate: filters.startDate,
            endDate: filters.endDate,
            status: filters.status && filters.status !== 'ALL' ? filters.status : undefined,
            branchId: selectedBranch?.id,
            tableId: filters.tableId || undefined,
            paymentMethod: filters.paymentMethod || undefined,
            bankAccountId: filters.bankAccountId || undefined,
            completedBy: filters.completedBy || undefined,
            orderNumber: filters.orderNumber || undefined,
            maxTotalAmount,
            hasDeletionRequest: filters.hasDeletionRequest || undefined,
            sortBy: filters.sortBy || 'created_at',
            sortOrder: filters.sortOrder || 'desc',
            page,
            limit,
          };

      const response = await orderAPI.getOrders(params);
      const payload = response?.data || response;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : payload?.orders || [];
      const normalized = list.map((order) => normalizeOrderRecord(order)).filter(Boolean);

      setOrders(normalized);
      // #071245: `meta` nằm trong payload (đường `response.data`), lấy thêm ở đó —
      // chế độ soi doanh thu cần `meta.filtered_total_amount` để hiện tổng ĐÚNG
      // của cả tập, không phải tổng của mỗi trang đang xem.
      setMeta(response?.meta || response?.pagination || payload?.meta || payload?.pagination || null);
    } catch (error) {
      console.error('Cannot fetch orders', error);
      toast.error('Không tải được danh sách đơn hàng');
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit, selectedBranch]);

  useEffect(() => {
    const loadBankAccounts = async () => {
      try {
        const response = await bankAccountAPI.getBankAccounts();
        const payload = response?.data || response;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : payload?.bank_accounts || [];
        setBankAccounts(list.filter(Boolean));
      } catch (e) {
        console.error('Cannot load bank accounts', e);
      }
    };
    loadBankAccounts();
  }, []);

  // #182157: danh sách NV của chi nhánh đang xem để lọc "NV thanh toán".
  useEffect(() => {
    if (!selectedBranch?.id) {
      setPayerOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await employeeAPI.getEmployeesByBranch(selectedBranch.id, { limit: 200 });
        const payload = response?.data || response;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.employees)
            ? payload.employees
            : Array.isArray(payload)
              ? payload
              : [];
        if (!cancelled) setPayerOptions(list.filter((e) => e?.id && e?.full_name));
      } catch (e) {
        console.error('Cannot load employees for payer filter', e);
        if (!cancelled) setPayerOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBranch?.id]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  /**
   * #071245 — nhãn người đọc được của dòng doanh thu đang soi. `CASH` /
   * `UNKNOWN_BANK` là 2 nhóm không gắn tài khoản, phải gọi tên khác nhau vì trên
   * bảng doanh thu chúng cùng hiện "-" ở cột số tài khoản và tên tài khoản.
   */
  const bankBucketLabel = useMemo(() => {
    const key = filters.bankBucket;
    if (!key) return '';
    if (key === 'CASH') return 'Tiền mặt (đơn không gắn tài khoản nhận)';
    if (key === 'UNKNOWN_BANK') return 'Đơn chuyển khoản chưa gắn tài khoản nhận';
    const acc = bankAccounts.find((a) => a.id === key);
    if (!acc) return `Tài khoản ${key}`;
    return [acc.account_name, acc.account_no].filter(Boolean).join(' — ') || `Tài khoản ${key}`;
  }, [filters.bankBucket, bankAccounts]);

  const summary = useMemo(() => {
    const total = orders.length;
    const completed = orders.filter((order) => order.status === 'COMPLETED').length;
    const cancelled = orders.filter((order) => order.status === 'CANCELLED').length;
    const inProgress = orders.filter((order) =>
      ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'SERVED'].includes(order.status),
    ).length;
    const revenue = orders.reduce((sum, order) => sum + (order.status === 'COMPLETED' ? order.totalAmount : 0), 0);
    return { total, completed, cancelled, inProgress, revenue };
  }, [orders]);

  // #071245 — ở chế độ soi doanh thu, thẻ tổng phải là tổng của TOÀN BỘ tập đơn do
  // máy chủ chốt (`meta.filtered_total_amount`), không phải tổng cộng dồn từ trang
  // đang xem và không lọc theo trạng thái COMPLETED — có vậy mới khớp đúng con số
  // trên bảng "Doanh thu theo tài khoản ngân hàng".
  const bucketTotalAmount = bankBucketMode ? (meta?.filtered_total_amount ?? null) : null;

  // #014327 (lỗi tồn đọng #071245) — 3 thẻ đếm ở chế độ soi doanh thu phải là số
  // của TOÀN BỘ tập đã khớp báo cáo do máy chủ chốt (`meta`), không phải của riêng
  // trang đang xem: máy khách chỉ giữ tối đa `limit` đơn/trang nên tự đếm ở đây sẽ
  // ra số của trang 1 trong khi "Tổng đơn" đã là số của cả tập. Chế độ thường giữ
  // nguyên cách tính cũ từ `summary` (tính trên trang đang xem, đúng như trước).
  const totalCount = bankBucketMode
    ? (meta?.total ?? summary.total)
    : summary.total;
  const completedCount = bankBucketMode ? (meta?.completed_count ?? summary.completed) : summary.completed;
  const inProgressCount = bankBucketMode ? (meta?.in_progress_count ?? summary.inProgress) : summary.inProgress;

  const handleFilterChange = (field, value) => {
    const urlField = URL_FIELDS[field];
    if (urlField) {
      setUrlFilters({ [urlField]: value });
      return;
    }
    // Bộ lọc cục bộ vẫn phải tuân luật 4: đổi lọc thì về trang 1.
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setLocalFilters(getDefaultLocalFilters());
    resetUrlFilters();
  };

  // #071245 — gỡ riêng bộ lọc "soi doanh thu", giữ nguyên kỳ đang xem để người
  // dùng thấy ngay danh sách đầy đủ của đúng khoảng thời gian đó.
  const handleClearBankBucket = () => setUrlFilters({ bank_bucket: '' });

  const handleExportCsv = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.warning('Chọn khoảng ngày (Từ ngày - Đến ngày) để xuất CSV');
      return;
    }
    try {
      setExportingCsv(true);
      const maxTotalAmount = parseOptionalMaxTotalAmount(filters.maxTotalAmount);
      const blob = await orderAPI.exportOrdersCsv({
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status && filters.status !== 'ALL' ? filters.status : undefined,
        branchId: selectedBranch?.id,
        tableId: filters.tableId || undefined,
        paymentMethod: filters.paymentMethod || undefined,
        bankAccountId: filters.bankAccountId || undefined,
        maxTotalAmount,
        sortBy: filters.sortBy || 'created_at',
        sortOrder: filters.sortOrder || 'desc',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${filters.startDate}_${filters.endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Đã xuất file CSV');
    } catch (e) {
      console.error('Export CSV failed', e);
      toast.error(e?.message || 'Không thể xuất CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  /**
   * #232308 — lượt bấm nút "Xem số điện thoại". Máy chủ ghi nhật ký đích danh rồi
   * mới trả số; nhật ký chết thì vẫn cho xem (fail-open) nhưng phải báo ra màn hình.
   */
  const handleRevealPhone = async (orderId) => {
    try {
      setRevealingPhone(true);
      const res = await orderAPI.revealCustomerPhone(orderId);
      const data = res?.data || res;
      setRevealedPhone(data?.customer_phone || null);
      if (data && data.logged === false) {
        toast.error('Số vẫn hiện nhưng lượt xem này CHƯA ghi được vào nhật ký — báo kỹ thuật giúp.');
      }
    } catch (e) {
      toast.error(e?.message || 'Không xem được SĐT khách hàng');
    } finally {
      setRevealingPhone(false);
    }
  };

  const loadLinkedOrder = (orderId) => {
    setDetailOrder(null);
    // Mở khoá là theo TỪNG ĐƠN — chuyển sang đơn khác thì số cũ phải biến mất.
    setRevealedPhone(null);
    setDetailLoading(true);
    setAuditLoading(true);
    setDeletionLoading(true);
    setOrderAuditEvents([]);
    setDeletionRequests([]);
    setDetailLoadPaymentProof(false);
    Promise.all([
      orderAPI.getOrderById(orderId),
      orderStaffAuditAPI
        .getEvents({ order_id: orderId, branch_id: selectedBranch?.id, page: 1, limit: 80 })
        .catch(() => ({ data: { data: [] } })),
      orderApprovalAPI.getByOrderId(orderId).catch(() => ({ data: [] })),
    ])
      .then(([orderRes, auditRes, deletionRes]) => {
        setDetailOrder(orderRes?.data || orderRes);
        const ev = pickAuditEvents(auditRes);
        setOrderAuditEvents(Array.isArray(ev) ? ev : []);
        const dr = deletionRes?.data ?? [];
        setDeletionRequests(pickDeletionRequests(dr));
      })
      .catch(() => toast.error('Không tải được đơn hàng liên kết'))
      .finally(() => {
        setDetailLoading(false);
        setAuditLoading(false);
        setDeletionLoading(false);
      });
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header my-2">
          <div className="mc-page-header-title">
            <h1>Quản lý đơn hàng</h1>
          </div>
          <div className="mc-page-header-actions d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={handleResetFilters}
              disabled={loading}
            >
              Đặt lại bộ lọc
            </Button>
            <Button
              variant="primary"
              onClick={fetchOrders}
              disabled={loading}
            >
              {loading ? 'Đang tải...' : 'Làm mới'}
            </Button>
            <Button
              variant="outline-info"
              onClick={() => setLoadAllProofImages(true)}
            >
              Mở ảnh
            </Button>
            <Button
              variant="outline-success"
              onClick={handleExportCsv}
              disabled={exportingCsv || !filters.startDate || !filters.endDate}
            >
              {exportingCsv ? 'Đang xuất...' : 'Xuất CSV'}
            </Button>
          </div>
        </div>

        {/* #071245 — nói rõ đang lọc theo cái gì, và cho gỡ về danh sách đầy đủ. */}
        {bankBucketMode && (
          <Alert
            variant="info"
            className="d-flex flex-wrap align-items-center gap-2"
            data-testid="bank-bucket-banner"
          >
            <span>🏦 Đang xem các đơn cấu thành doanh thu của:</span>
            <Badge bg="primary" className="text-wrap">
              {bankBucketLabel}
            </Badge>
            <Badge bg="secondary">
              {filters.startDate} → {filters.endDate}
            </Badge>
            {meta?.filtered_total_amount != null && (
              <Badge bg="success">Tổng {formatCurrency(meta.filtered_total_amount)}</Badge>
            )}
            <Button
              size="sm"
              variant="outline-secondary"
              className="ms-auto"
              onClick={handleClearBankBucket}
            >
              Gỡ bộ lọc, xem tất cả đơn
            </Button>
            <div className="w-100">
              <small className="text-muted">
                Tập đơn này lấy đúng theo luật của báo cáo doanh thu (đơn đã thanh toán, gom theo
                ngày của CA), nên các bộ lọc trạng thái / phương thức / bàn tạm thời không áp dụng.
              </small>
            </div>
          </Alert>
        )}

        <Row className="mb-4">
          <Col
            md={3}
            className="mb-3"
          >
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <small className="text-muted text-uppercase">Tổng đơn</small>
                <h3 className="mb-0">{totalCount}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col
            md={3}
            className="mb-3"
          >
            <Card className="h-100 shadow-sm border-success">
              <Card.Body>
                <small className="text-muted text-uppercase">Hoàn thành</small>
                {/* #014327 (lỗi tồn đọng #071245): đếm trên TOÀN BỘ tập đã khớp
                    báo cáo (`completedCount`), không phải riêng trang đang xem. */}
                <h3 className="text-success mb-0">{completedCount}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col
            md={3}
            className="mb-3"
          >
            <Card className="h-100 shadow-sm border-warning">
              <Card.Body>
                <small className="text-muted text-uppercase">Đang xử lý</small>
                {/* #014327 (lỗi tồn đọng #071245): như thẻ "Hoàn thành" — đếm trên
                    toàn bộ tập đã khớp (`inProgressCount`). */}
                <h3 className="text-warning mb-0">{inProgressCount}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col
            md={3}
            className="mb-3"
          >
            <Card className="h-100 shadow-sm border-primary">
              <Card.Body>
                <small className="text-muted text-uppercase">
                  {bankBucketMode ? 'Tổng tiền (khớp báo cáo)' : 'Doanh thu (hoàn thành)'}
                </small>
                <h4 className="mb-0" data-testid="orders-total-amount">
                  {formatCurrency(bucketTotalAmount ?? summary.revenue)}
                </h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4 shadow-sm">
          <Card.Header>
            <h5 className="mb-0">Bộ lọc đơn hàng</h5>
          </Card.Header>
          <Card.Body>
            <InvalidParamsNotice
              ignored={ignored}
              onDismiss={dismissIgnored}
            />
            <DateRangePicker
              fromDate={filters.startDate}
              toDate={filters.endDate}
              defaultPreset="last7days"
              onChange={(from, to) => setUrlFilters({ from, to })}
            />
            <Row className="g-3 mt-2">
              <Col lg={3}>
                <Form.Label>Trạng thái</Form.Label>
                <Form.Select
                  value={filters.status}
                  disabled={bankBucketMode}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="ALL">Tất cả</option>
                  {ORDER_STATUS.map((status) => (
                    <option
                      key={status}
                      value={status}
                    >
                      {translateLabel(ORDER_STATUS_LABELS, status, status)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={3}>
                <Form.Label>Phương thức thanh toán</Form.Label>
                <Form.Select
                  value={filters.paymentMethod}
                  disabled={bankBucketMode}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                >
                  <option value="">Tất cả</option>
                  {PAYMENT_METHODS.map((method) => (
                    <option
                      key={method}
                      value={method}
                    >
                      {translateLabel(PAYMENT_METHOD_LABELS, method, method)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={3}>
                <Form.Label>Tài khoản nhận</Form.Label>
                <Form.Select
                  value={filters.bankAccountId}
                  disabled={bankBucketMode}
                  onChange={(e) => handleFilterChange('bankAccountId', e.target.value)}
                >
                  <option value="">Tất cả tài khoản</option>
                  {bankAccounts.map((acc) => (
                    <option
                      key={acc.id}
                      value={acc.id}
                    >
                      {[acc.short_name, acc.account_no, acc.account_name].filter(Boolean).join(' - ')}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={3}>
                <Form.Label>NV thanh toán</Form.Label>
                <Form.Select
                  value={filters.completedBy}
                  disabled={bankBucketMode}
                  onChange={(e) => handleFilterChange('completedBy', e.target.value)}
                >
                  <option value="">Tất cả nhân viên</option>
                  {payerOptions.map((emp) => (
                    <option
                      key={emp.id}
                      value={emp.id}
                    >
                      {emp.full_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={3}>
                <Form.Label>Bàn</Form.Label>
                <Form.Control
                  value={filters.tableId}
                  disabled={bankBucketMode}
                  onChange={(e) => handleFilterChange('tableId', e.target.value)}
                  placeholder="VD: A-01"
                />
              </Col>
              <Col lg={3}>
                <Form.Label>Mã đơn</Form.Label>
                <Form.Control
                  value={filters.orderNumber}
                  disabled={bankBucketMode}
                  onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
                  placeholder="Nhập mã đơn hàng"
                />
              </Col>
              <Col lg={3}>
                <Form.Label>Tổng tiền tối đa (VND)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step={1000}
                  value={filters.maxTotalAmount}
                  disabled={bankBucketMode}
                  onChange={(e) => handleFilterChange('maxTotalAmount', e.target.value)}
                  placeholder="VD: 500000 — đơn ≤ số này"
                />
                <Form.Text className="text-muted">Để trống = không giới hạn</Form.Text>
              </Col>
              <Col lg={2} className="d-flex align-items-center pt-4">
                <Form.Check
                  type="switch"
                  id="hasDeletionRequest"
                  label={<span className="fw-semibold text-danger">Có yêu cầu xóa</span>}
                  checked={!!filters.hasDeletionRequest}
                  disabled={bankBucketMode}
                  onChange={(e) => handleFilterChange('hasDeletionRequest', e.target.checked)}
                />
              </Col>
              <Col lg={2}>
                <Form.Label>Sắp xếp theo</Form.Label>
                <Form.Select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                >
                  {SORT_FIELDS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={2}>
                <Form.Label>Thứ tự</Form.Label>
                <Form.Select
                  value={filters.sortOrder}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                >
                  {SORT_ORDERS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Danh sách đơn hàng</h5>
              <small className="text-muted">
                {loading ? 'Đang tải...' : `Hiển thị ${orders.length} đơn`}
                {meta?.total ? ` / ${meta.total}` : ''}
              </small>
            </div>
            {loading && (
              <Spinner
                animation="border"
                size="sm"
              />
            )}
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table
                hover
                className="mb-0"
              >
                <thead>
                  <tr>
                    <th>Mã đơn</th>
                    <th>Bàn</th>
                    <th>Khách hàng</th>
                    <th>Trạng thái</th>
                    <th>Thanh toán</th>
                    <th>NV thanh toán</th>
                    <th>Tổng tiền</th>
                    <th>Thời gian</th>
                    <th>Ảnh</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && orders.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="text-center py-4 text-muted"
                      >
                        Không có đơn hàng nào theo bộ lọc hiện tại.
                      </td>
                    </tr>
                  )}
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td className="fw-semibold">{order.orderNumber || order.id}</td>
                      <td>{order.tableLabel}</td>
                      <td>
                        <div className="fw-semibold">{order.customerName || '—'}</div>
                        {order.customerPhone ? (
                          <small className="text-muted d-block">{order.customerPhone}</small>
                        ) : null}
                        {order.memberCount != null ? (
                          <small className="text-muted d-block">👥 {order.memberCount} người</small>
                        ) : null}
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <Badge bg={statusVariant[order.status] || 'secondary'}>
                            {translateLabel(ORDER_STATUS_LABELS, order.status, order.status)}
                          </Badge>
                          {order.mergedFromOrderIds?.length > 0 && (
                            <Badge bg="info" title={`Gộp từ ${order.mergedFromOrderIds.length} đơn`}>
                              🔀 Gộp {order.mergedFromOrderIds.length} đơn
                            </Badge>
                          )}
                          {order.mergedIntoOrderId && (
                            <Badge bg="secondary" title={order.cancelReason ?? 'Đã gộp vào đơn khác'}>
                              🔀 Đã gộp
                            </Badge>
                          )}
                          {order.deletion_requests?.length > 0 && (() => {
                            const hasDeleteOrder = order.deletion_requests.some(r => r.request_type === 'DELETE_ORDER');
                            const hasDeleteItem = order.deletion_requests.some(r => r.request_type === 'DELETE_ORDER_ITEM');
                            const hasPending = order.deletion_requests.some(r => r.status === 'PENDING');
                            return (
                              <Badge bg={hasPending ? 'danger' : 'secondary'} title={
                                order.deletion_requests.map(r =>
                                  `${r.request_type === 'DELETE_ORDER' ? 'Xóa đơn' : 'Xóa món'}${r.menu_item_name ? ` (${r.menu_item_name})` : ''}: ${r.status}`
                                ).join('\n')
                              }>
                                {hasDeleteOrder ? '🗑 Xóa đơn' : `🗑 Xóa món`}
                                {hasDeleteItem && hasDeleteOrder ? ' + món' : ''}
                              </Badge>
                            );
                          })()}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <Badge bg={paymentVariant[order.paymentStatus] || 'secondary'}>
                            {translateLabel(
                              PAYMENT_STATUS_LABELS,
                              order.paymentStatus || 'PENDING',
                              order.paymentStatus || 'PENDING',
                            )}
                          </Badge>
                          <small className="text-muted">
                            {translateLabel(PAYMENT_METHOD_LABELS, order.paymentMethod, order.paymentMethod || 'N/A')}
                          </small>
                        </div>
                      </td>
                      <td>
                        {order.paymentStatus === 'PAID' ? (
                          order.completedByName || (order.completedBy ? 'NV đã nghỉ/không tra được' : '—')
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{formatCurrency(order.totalAmount)}</td>
                      <td>
                        {formatDateTime(order.createdAt)}{' '}
                        {order.paidAt ? ` - ${format(new Date(order.paidAt), 'HH:mm')}` : ''}
                      </td>
                      <td
                        className="align-middle"
                        style={{ minBlockSize: 120, paddingBlock: 8 }}
                      >
                        {order.paymentProofUrl ? (
                          loadAllProofImages ? (
                            <a
                              href={order.paymentProofUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="d-inline-block"
                            >
                              <img
                                src={order.paymentProofUrl}
                                alt={`Proof ${order.orderNumber}`}
                                className="rounded border"
                                width={100}
                                height={100}
                                style={{ objectFit: 'cover' }}
                              />
                            </a>
                          ) : (
                            <div
                              className="rounded border bg-light d-flex align-items-center justify-content-center text-muted"
                              style={{ inlineSize: 100, blockSize: 100, minInlineSize: 100 }}
                            >
                              <span className="small">📷</span>
                            </div>
                          )
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setShowDetailModal(true);
                            setDetailOrder(null);
                            // #232308: mở khoá SĐT là theo từng đơn, không mang sang đơn khác.
                            setRevealedPhone(null);
                            setOrderAuditEvents([]);
                            setDeletionRequests([]);
                            setDetailLoadPaymentProof(false);
                            setDetailLoading(true);
                            setAuditLoading(true);
                            setDeletionLoading(true);
                            Promise.all([
                              orderAPI.getOrderById(order.id),
                              orderStaffAuditAPI
                                .getEvents({
                                  order_id: order.id,
                                  branch_id: selectedBranch?.id,
                                  page: 1,
                                  limit: 80,
                                })
                                .catch(() => ({ data: { data: [] } })),
                              orderApprovalAPI
                                .getByOrderId(order.id)
                                .catch(() => ({ data: [] })),
                            ])
                              .then(([orderRes, auditRes, deletionRes]) => {
                                const data = orderRes?.data || orderRes;
                                setDetailOrder(data);
                                const ev = pickAuditEvents(auditRes);
                                setOrderAuditEvents(Array.isArray(ev) ? ev : []);
                                const dr = deletionRes?.data ?? [];
                                setDeletionRequests(pickDeletionRequests(dr));
                              })
                              .catch((error) => {
                                console.error('Cannot fetch order detail', error);
                                toast.error('Không tải được chi tiết đơn hàng');
                                setShowDetailModal(false);
                              })
                              .finally(() => {
                                setDetailLoading(false);
                                setAuditLoading(false);
                                setDeletionLoading(false);
                              });
                          }}
                        >
                          Xem chi tiết
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {meta && (
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 border-top">
                <div>
                  Trang {page}/{meta.total_pages || meta.totalPages || 1} · Tổng {meta.total || orders.length} đơn
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    size="sm"
                    value={limit}
                    onChange={(e) => setUrlFilters({ limit: Number(e.target.value) })}
                  >
                    {[10, 20, 50].map((size) => (
                      <option
                        key={size}
                        value={size}
                      >
                        {size}/trang
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  >
                    Trước
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={meta.total_pages ? page >= meta.total_pages : false}
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Sau
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Chi tiết đơn hàng</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}

          {!detailLoading && !detailOrder && <div className="text-muted">Không có dữ liệu đơn hàng.</div>}

          {detailOrder && (
            <>
              <div className="mb-3">
                <h5 className="mb-1">{detailOrder.order_number || detailOrder.id}</h5>
                <div className="d-flex gap-2 flex-wrap">
                  <Badge bg={statusVariant[detailOrder.status] || 'secondary'}>
                    {translateLabel(ORDER_STATUS_LABELS, detailOrder.status, detailOrder.status)}
                  </Badge>
                  <Badge bg={paymentVariant[detailOrder.payment_status] || 'secondary'}>
                    {translateLabel(PAYMENT_STATUS_LABELS, detailOrder.payment_status, detailOrder.payment_status)}
                  </Badge>
                  <Badge
                    bg="light"
                    text="dark"
                  >
                    {translateLabel(ORDER_TYPE_LABELS, detailOrder.order_type, detailOrder.order_type)}
                  </Badge>
                </div>
              </div>

              <div className="mb-3">
                <strong>Chi nhánh:</strong>{' '}
                {detailOrder.branch?.name || detailOrder.branch_name || detailOrder.branch_id}
              </div>
              <div className="mb-3">
                <strong>Bàn:</strong>{' '}
                {detailOrder.table?.number || detailOrder.table?.name || detailOrder.table_id || 'N/A'}
              </div>
              <div className="mb-3">
                <strong>Tài khoản nhận:</strong>{' '}
                {detailOrder.bank_account
                  ? `${detailOrder.bank_account.bank_name} - ${detailOrder.bank_account.account_no} (${detailOrder.bank_account.account_name})`
                  : 'N/A'}
              </div>
              <div className="mb-2">
                <strong>Khách hàng:</strong> {detailOrder.customer_name || detailOrder.customerName || 'Khách lẻ'}
                {/* #232308 — lệnh sếp 02/09/2026 (nút `sdt_nut_moi_hien`): SĐT khách
                    phải BẤM NÚT mới hiện, và ADMIN/MANAGER cũng đi qua đúng nút này
                    ("không phải ai xem cũng như ai"). Số dưới đây đã được MÁY CHỦ che
                    sẵn — trang này không tự che và cũng không tự bỏ che. */}
                {(detailOrder.customer_phone || detailOrder.customerPhone) && (
                  <>
                    {' '}
                    ({revealedPhone || detailOrder.customer_phone || detailOrder.customerPhone})
                    {!revealedPhone && !detailOrder.phone_reveal?.revealed && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="ms-2 py-0"
                        disabled={revealingPhone}
                        onClick={() => handleRevealPhone(detailOrder.id)}
                      >
                        {revealingPhone ? 'Đang xem…' : 'Xem số điện thoại'}
                      </Button>
                    )}
                  </>
                )}
              </div>
              <div className="mb-3">
                <strong>Số khách:</strong>{' '}
                {detailOrder.member_count != null ? `${detailOrder.member_count} người` : 'N/A'}
              </div>

              {/* Section A: Đây là đơn gộp — show các đơn nguồn */}
              {detailOrder.merged_from_orders?.length > 0 && (
                <div className="mb-3">
                  <h6 className="mb-2">🔀 Gộp từ các đơn</h6>
                  <Table size="sm" className="mb-0 border rounded">
                    <thead className="table-light">
                      <tr>
                        <th>Số đơn</th>
                        <th>Thời gian</th>
                        <th>Tổng tiền</th>
                        <th>Trạng thái</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailOrder.merged_from_orders.map((o) => (
                        <tr key={o.id}>
                          <td className="fw-semibold small">{o.order_number}</td>
                          <td className="small text-nowrap">{formatDateTime(o.created_at)}</td>
                          <td className="small">{formatCurrency(o.total_amount)}</td>
                          <td className="small">
                            <Badge bg={statusVariant[o.status] ?? 'secondary'}>
                              {translateLabel(ORDER_STATUS_LABELS, o.status, o.status)}
                            </Badge>
                          </td>
                          <td>
                            <Button size="sm" variant="outline-secondary"
                              onClick={() => loadLinkedOrder(o.id)}>
                              Xem
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Section B: Đơn này bị cancel do gộp — show đơn đích */}
              {detailOrder.merged_into_order && (
                <div className="alert alert-secondary py-2 mb-3 small d-flex align-items-center gap-2 flex-wrap">
                  <span>
                    🔀 Đơn này đã được gộp vào{' '}
                    <strong>{detailOrder.merged_into_order.order_number}</strong>{' '}
                    ({formatDateTime(detailOrder.merged_into_order.created_at)})
                  </span>
                  <Button size="sm" variant="outline-secondary"
                    onClick={() => loadLinkedOrder(detailOrder.merged_into_order.id)}>
                    Xem đơn gộp →
                  </Button>
                </div>
              )}

              {/* Section C: Cancel tay — show lý do */}
              {detailOrder.cancel_reason && !detailOrder.merged_into_order && (
                <div className="alert alert-warning py-2 mb-3 small">
                  ⚠️ <strong>Lý do hủy:</strong> {detailOrder.cancel_reason}
                </div>
              )}

              <h6 className="mt-2">Nhật ký thao tác (nhân viên — món)</h6>
              <div className="border rounded mb-3 bg-light">
                {auditLoading && (
                  <div className="p-2 small text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Đang tải nhật ký…
                  </div>
                )}
                {!auditLoading && (!orderAuditEvents || orderAuditEvents.length === 0) && (
                  <div className="p-2 small text-muted">Chưa có sự kiện hoặc chưa có quyền xem.</div>
                )}
                {!auditLoading && orderAuditEvents.length > 0 && (
                  <Table striped size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Thời gian</th>
                        <th>Hành động</th>
                        <th>Nhân viên</th>
                        <th>Ca</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderAuditEvents.map((ev) => (
                        <tr key={ev.id}>
                          <td className="text-nowrap small">{formatDateTime(ev.created_at)}</td>
                          <td className="small">
                            {ORDER_STAFF_ACTION_LABELS[ev.action_type] || ev.action_type}
                          </td>
                          <td className="small">{ev.actor?.full_name || ev.actor?.username || ev.actor_id || '—'}</td>
                          <td className="small text-muted">
                            {ev.shift?.id ? `${formatDateTime(ev.shift.start_time)} → ${formatDateTime(ev.shift.end_time)}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>

              {/* Deletion requests section */}
              <h6 className="mt-2">Yêu cầu xóa đơn / xóa món</h6>
              <div className="border rounded mb-3 bg-light">
                {deletionLoading && (
                  <div className="p-2 small text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Đang tải…
                  </div>
                )}
                {!deletionLoading && deletionRequests.length === 0 && (
                  <div className="p-2 small text-muted">Không có yêu cầu xóa nào.</div>
                )}
                {!deletionLoading && deletionRequests.length > 0 && (
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Loại</th>
                        <th>Món</th>
                        <th>NV yêu cầu</th>
                        <th>Lý do</th>
                        <th>NV duyệt</th>
                        <th>Trạng thái</th>
                        <th>Thời gian</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deletionRequests.map((dr) => (
                        <tr key={dr.id}>
                          <td className="small">
                            <Badge bg={dr.request_type === 'DELETE_ORDER' ? 'danger' : 'warning'}>
                              {dr.request_type === 'DELETE_ORDER' ? 'Xóa đơn' : 'Xóa món'}
                            </Badge>
                          </td>
                          <td className="small">{dr.request_data?.menu_item_name ?? '—'}</td>
                          <td className="small">{dr.requester_name ?? '—'}</td>
                          <td className="small" style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={dr.reason ?? ''}>
                            {dr.reason ?? '—'}
                          </td>
                          <td className="small">{dr.approver_name ?? '—'}</td>
                          <td className="small">
                            <Badge bg={{ PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', EXPIRED: 'secondary' }[dr.status] ?? 'secondary'}>
                              {{ PENDING: 'Chờ duyệt', APPROVED: 'Đã duyệt', REJECTED: 'Từ chối', EXPIRED: 'Hết hạn' }[dr.status] ?? dr.status}
                            </Badge>
                          </td>
                          <td className="small text-nowrap">{formatDateTime(dr.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>

              <h6>Món gọi</h6>
              <div className="border rounded mb-3">
                <Table
                  striped
                  bordered
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th>Món</th>
                      <th>Số lượng</th>
                      <th>Tùy chọn</th>
                      <th>Thành tiền</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailOrder.items || []).map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-semibold">
                            {item.menu_item?.name || item.menu_item_name || item.menu_item_id}
                          </div>
                          {(item.customer_phone || item.customerPhone) && (
                            <small className="text-muted d-block">
                              SĐT món: {item.customer_phone || item.customerPhone}
                            </small>
                          )}
                          {item.notes && (
                            <small className="text-muted d-block">Ghi chú: {item.notes}</small>
                          )}
                        </td>
                        <td>{item.quantity}</td>
                        <td>
                          {item.selected_modifiers ? (
                            <>
                              {item.selected_modifiers_display ? (
                                <ul className="mb-0 small ps-3">
                                  {Object.entries(item.selected_modifiers_display).map(([groupName, selection]) => (
                                    <li key={groupName}>
                                      <strong>{groupName}:</strong>{' '}
                                      {Array.isArray(selection) ? selection.join(', ') : selection}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <pre className="mb-0 small bg-light p-2 rounded">
                                  {JSON.stringify(item.selected_modifiers, null, 2)}
                                </pre>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{formatCurrency(item.subtotal || item.quantity * item.unit_price)}</td>
                      </tr>
                    ))}
                    {(!detailOrder.items || detailOrder.items.length === 0) && (
                      <tr>
                        <td
                          colSpan={4}
                          className="text-center text-muted"
                        >
                          Không có món nào.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between">
                <div>
                  <div>Subtotal: {formatCurrency(detailOrder.subtotal)}</div>
                  <div>Giảm giá: {formatCurrency(detailOrder.discount_amount)}</div>
                  <div>Thuế: {formatCurrency(detailOrder.tax_amount)}</div>
                  <div>Phụ thu: {formatCurrency(detailOrder.surcharge_amount)}</div>
                </div>
                <div className="text-end">
                  <div className="fs-5 fw-bold">Tổng thanh toán: {formatCurrency(detailOrder.total_amount)}</div>
                  <small className="text-muted">
                    Phương thức:{' '}
                    {translateLabel(
                      PAYMENT_METHOD_LABELS,
                      detailOrder.payment_method,
                      detailOrder.payment_method || 'N/A',
                    )}
                  </small>
                  {detailOrder.payment_status === 'PAID' && (
                    <div className="small">
                      <span className="text-muted">NV thanh toán: </span>
                      <strong>
                        {detailOrder.completer?.full_name ||
                          (detailOrder.completed_by ? 'Không tra được tên' : '—')}
                      </strong>
                      {detailOrder.paid_at ? (
                        <span className="text-muted"> · {formatDateTime(detailOrder.paid_at)}</span>
                      ) : null}
                    </div>
                  )}
                  {detailOrder.payment_proof_url && (
                    <div className="mt-3">
                      <div className="small text-muted mb-1">Ảnh chứng từ thanh toán:</div>
                      {!detailLoadPaymentProof ? (
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded border bg-light d-flex align-items-center justify-content-center text-muted"
                            style={{ inlineSize: 48, blockSize: 48, minInlineSize: 48 }}
                          >
                            <span className="small">📷</span>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setDetailLoadPaymentProof(true)}
                          >
                            Xem tất cả ảnh
                          </Button>
                        </div>
                      ) : (
                        <a
                          href={detailOrder.payment_proof_url}
                          target="_blank"
                          rel="noreferrer"
                          className="d-inline-block"
                        >
                          <img
                            src={detailOrder.payment_proof_url}
                            alt="Payment proof"
                            className="rounded border"
                            width={220}
                            height={220}
                            style={{ objectFit: 'cover' }}
                          />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </PageLayout>
  );
};

export default OrderListPage;
